import { Check, Clock, Circle, Lock, ChevronRight, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { GoalMode } from "./GoalPicker";

export type NavigateTarget =
  | "tab1.step1"
  | "tab1.step2.sro"
  | "tab1.step2.audit"
  | "tab1.step3"
  | "tab1.step4"
  | "tab2.compute"
  | "tab2.bulkAccept"
  | "tab2.execute"
  | "tab-redirects"  // STOP-5: redirect proposals tab (mode='redirect')
  | "summary"        // STOP-2: read-only mode default target
  | null;

export type PhaseStatus = "done" | "in_progress" | "not_started" | "locked";

interface PhaseDef {
  key: "fetched" | "audit" | "match" | "decide" | "execute";
  label: string;
  status: PhaseStatus;
  detail?: string;
}

interface ExistingPostsHeaderProps {
  websiteName?: string;
  total: number;
  withSro: number;
  audited: number;
  classified: number;
  matchedCount: number;
  withFa: number;
  lockedActions: number;
  processedCount: number;
  withRankings: number;
  isAnyPending: boolean;
  onNavigateToStep: (target: NavigateTarget) => void;
  onOpenHelp?: () => void;
  // STOP-2 (2026-06-01): per goalMode dispatches do 1 z 3 deriveNextStep funkcji.
  // Default 'full_audit' = obecne zachowanie 1:1 (bit-by-bit kopia legacy deriveNextStep).
  goalMode?: GoalMode;
}

// KRYTYCZNE: deriveNextStepFullAudit MUSI BYĆ BIT-BY-BIT kopią legacy deriveNextStep
// (linie 41-59 sprzed STOP-2). Skopiowane literalnie, NIE refactor. PARITY guard.
function deriveNextStepFullAudit(p: {
  total: number;
  withSro: number;
  audited: number;
  matchedCount: number;
  classified: number;
  withFa: number;
  lockedActions: number;
}): { label: string; target: NavigateTarget } {
  if (p.total === 0) return { label: "Pobierz wpisy + SEO", target: "tab1.step1" };
  if (p.withSro < p.total) return { label: "Wyekstrahuj SRO", target: "tab1.step2.sro" };
  if (p.audited < p.withSro) return { label: "Audyt jakości", target: "tab1.step2.audit" };
  if (p.matchedCount === 0) return { label: "Matching nodów", target: "tab1.step3" };
  if (p.classified < p.total) return { label: "Klasyfikuj scope (LLM)", target: "tab1.step4" };
  if (p.withFa === 0) return { label: "Przelicz akcje (Decision Engine)", target: "tab2.compute" };
  if (p.lockedActions < p.withFa)
    return { label: "Zaakceptuj akcje", target: "tab2.bulkAccept" };
  return { label: "Wykonaj zatwierdzone", target: "tab2.execute" };
}

// STOP-2: Redirect mode — uproszczona ścieżka (Pobierz → Skanuj propozycje → Apply pending).
function deriveNextStepRedirect(p: { total: number }): { label: string; target: NavigateTarget } {
  if (p.total === 0) return { label: "Pobierz wpisy", target: "tab1.step1" };
  return { label: "Skanuj propozycje redirectów", target: "tab-redirects" };
}

// STOP-2: Read-only mode — brak akcji, default tab='summary'.
function deriveNextStepReadOnly(): { label: string; target: NavigateTarget } {
  return { label: "Tryb tylko-podgląd — brak akcji", target: "summary" };
}

function buildPhases(p: {
  total: number;
  withSro: number;
  audited: number;
  matchedCount: number;
  withFa: number;
  lockedActions: number;
  processedCount: number;
}): PhaseDef[] {
  const fetched: PhaseDef = {
    key: "fetched",
    label: "Pobrane",
    status: p.total > 0 ? "done" : "not_started",
    detail: p.total > 0 ? `${p.total}` : "—",
  };

  let auditStatus: PhaseStatus;
  if (p.total === 0) auditStatus = "locked";
  else if (p.withSro === p.total && p.audited === p.withSro && p.total > 0) auditStatus = "done";
  else if (p.withSro > 0 || p.audited > 0) auditStatus = "in_progress";
  else auditStatus = "not_started";
  const audit: PhaseDef = {
    key: "audit",
    label: "Audyt",
    status: auditStatus,
    detail: p.total > 0 ? `${p.audited}/${p.total}` : "—",
  };

  let matchStatus: PhaseStatus;
  if (p.total === 0) matchStatus = "locked";
  else if (p.matchedCount > 0) matchStatus = "done";
  else matchStatus = "not_started";
  const match: PhaseDef = {
    key: "match",
    label: "Match",
    status: matchStatus,
    detail: p.matchedCount > 0 ? `${p.matchedCount}` : "—",
  };

  let decideStatus: PhaseStatus;
  if (p.matchedCount === 0) decideStatus = "locked";
  else if (p.withFa > 0 && p.lockedActions === p.withFa) decideStatus = "done";
  else if (p.withFa > 0) decideStatus = "in_progress";
  else decideStatus = "not_started";
  const decide: PhaseDef = {
    key: "decide",
    label: "Decyzje",
    status: decideStatus,
    detail: p.withFa > 0 ? `${p.lockedActions}/${p.withFa}` : "—",
  };

  let execStatus: PhaseStatus;
  if (p.lockedActions === 0) execStatus = "locked";
  else if (p.processedCount > 0) execStatus = "done";
  else execStatus = "not_started";
  const execute: PhaseDef = {
    key: "execute",
    label: "Wykonane",
    status: execStatus,
    detail: p.processedCount > 0 ? `${p.processedCount}` : "—",
  };

  return [fetched, audit, match, decide, execute];
}

const STATUS_STYLE: Record<
  PhaseStatus,
  { dot: string; text: string; ring: string; Icon: typeof Check }
> = {
  done: {
    dot: "bg-emerald-500",
    text: "text-emerald-500",
    ring: "ring-emerald-500/40",
    Icon: Check,
  },
  in_progress: {
    dot: "bg-amber-500 animate-pulse",
    text: "text-amber-500",
    ring: "ring-amber-500/40",
    Icon: Clock,
  },
  not_started: {
    dot: "bg-zinc-600",
    text: "text-zinc-400",
    ring: "ring-zinc-500/30",
    Icon: Circle,
  },
  locked: {
    dot: "bg-slate-700",
    text: "text-slate-500",
    ring: "ring-slate-600/30",
    Icon: Lock,
  },
};

function PhaseIndicator({ phase, isLast }: { phase: PhaseDef; isLast: boolean }) {
  const style = STATUS_STYLE[phase.status];
  const Icon = style.Icon;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex flex-col items-center gap-1 min-w-[64px]">
        <div
          className={`flex items-center justify-center w-7 h-7 rounded-full ${style.dot} ring-2 ${style.ring}`}
          aria-label={`${phase.label}: ${phase.status}`}
        >
          <Icon className="h-4 w-4 text-white" strokeWidth={3} />
        </div>
        <div className="text-[11px] font-medium text-foreground leading-none whitespace-nowrap">
          {phase.label}
        </div>
        <div className={`text-[10px] leading-none ${style.text} font-mono`}>{phase.detail}</div>
      </div>
      {!isLast && (
        <div
          className={`h-px flex-1 min-w-[20px] ${
            phase.status === "done" ? "bg-emerald-500/60" : "bg-zinc-700"
          }`}
        />
      )}
    </div>
  );
}

export function ExistingPostsHeader({
  websiteName,
  total,
  withSro,
  audited,
  classified,
  matchedCount,
  withFa,
  lockedActions,
  processedCount,
  withRankings,
  isAnyPending,
  onNavigateToStep,
  onOpenHelp,
  goalMode = "full_audit",
}: ExistingPostsHeaderProps) {
  const phases = buildPhases({
    total,
    withSro,
    audited,
    matchedCount,
    withFa,
    lockedActions,
    processedCount,
  });
  // STOP-2: switch deriveNextStep per goalMode. 'full_audit' = legacy zachowanie 1:1.
  const next =
    goalMode === "redirect"
      ? deriveNextStepRedirect({ total })
      : goalMode === "read_only"
      ? deriveNextStepReadOnly()
      : deriveNextStepFullAudit({
          total,
          withSro,
          audited,
          matchedCount,
          classified,
          withFa,
          lockedActions,
        });

  const allDone =
    total > 0 &&
    withSro === total &&
    audited === withSro &&
    matchedCount > 0 &&
    withFa > 0 &&
    lockedActions === withFa &&
    processedCount > 0;

  return (
    <div className="rounded-xl border border-violet-500/20 bg-gradient-to-br from-violet-500/5 via-background to-background p-4 mb-4 shadow-sm">
      {/* Top row: domain + rankings + help */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-foreground truncate">
            📂 Istniejące posts
          </span>
          {websiteName && (
            <Badge variant="outline" className="text-[11px] font-mono">
              {websiteName}
            </Badge>
          )}
          {total > 0 && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {total} wpisów
              {withRankings > 0 && (
                <>
                  {" · "}
                  <span className="text-emerald-500 font-medium">{withRankings} z rankingami</span>
                </>
              )}
            </span>
          )}
        </div>
        {onOpenHelp && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onOpenHelp}
            className="text-xs gap-1.5 h-7"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            Pomoc
          </Button>
        )}
      </div>

      {/* 5 phase indicators */}
      <div className="flex items-center justify-between gap-1 mb-4 overflow-x-auto pb-1">
        {phases.map((phase, idx) => (
          <PhaseIndicator key={phase.key} phase={phase} isLast={idx === phases.length - 1} />
        ))}
      </div>

      {/* Next step CTA */}
      <div className="flex items-center justify-between gap-3 pt-3 border-t border-border/40 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {allDone ? (
            <>
              <Check className="h-4 w-4 text-emerald-500 shrink-0" />
              <span className="text-sm font-medium text-emerald-500">
                Wszystkie kroki ukończone — gotowe.
              </span>
            </>
          ) : (
            <>
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Następny krok
              </span>
              <span className="text-sm font-medium text-foreground truncate">{next.label}</span>
            </>
          )}
        </div>
        {!allDone && next.target && (
          <Button
            type="button"
            size="sm"
            onClick={() => onNavigateToStep(next.target)}
            disabled={isAnyPending}
            className="gap-1.5 bg-violet-600 hover:bg-violet-700 text-white"
          >
            {isAnyPending ? "Operacja w toku..." : "Przejdź"}
            {!isAnyPending && <ChevronRight className="h-4 w-4" />}
          </Button>
        )}
      </div>
    </div>
  );
}
